import classnames from 'classnames';
import React from 'react';

import { useSessionApi } from '../api/useSessionApi';
import { useAppContext } from '../AppContext';
import { Button } from '../Button';
import { appConfig } from '../config';
import { LoginForm } from '../form/LoginForm';
import { t } from '../i18n/i18n';
import { Icon } from '../Icon';
import { PageWithSubpageContext } from '../PageWithSubpageContext';
import { PageWithSubpageHeader } from '../PageWithSubpageHeader';
import { NavLink, Route, Routes } from '../router/router';
import { useMenu } from '../useMenu';
import { usePageByCode } from '../usePageByCode';
import { canUserAccessContent, getQualifiersFromTags, renderUI } from '../util';
import { AccountPageProps } from './AccountPage';
import { AccountCouponsSubpage } from './accountSubpages/AccountCouponsSubpage';
import { AccountCreditCardsSubpage } from './accountSubpages/AccountCreditCardsSubpage';
import { AccountHelpSubpage } from './accountSubpages/AccountHelpSubpage';
import { AccountInfoSubpage } from './accountSubpages/AccountInfoSubpage';
import { AccountLoyaltySubpage } from './accountSubpages/AccountLoyaltySubpage';
import { AccountOrdersSubpage } from './accountSubpages/AccountOrdersSubpage';
import { AccountPrivacySubpage } from './accountSubpages/AccountPrivacySubpage';
import { AccountQuickReorderSubpage } from './accountSubpages/AccountQuickReorderSubpage';
import { AccountReferralSubpage } from './accountSubpages/AccountReferralSubpage';
import { AccountTaxBenefitsSubpage } from './accountSubpages/AccountTaxBenefitsSubpage';
import { AccountWishlistSubpage } from './accountSubpages/AccountWishlistSubpage';

export const AccountPageA = ({ customMappings, enableReceiverColumn }: AccountPageProps) => {
  const { routeInfo, session } = useAppContext();
  const { logout } = useSessionApi();

  console.clear();
  console.log('routeInfo', routeInfo);

  const accountMenu = useMenu('MENU_ACCOUNT');
  const page = usePageByCode('SITE_CCN_AREA', { populate: { children: true } });

  const menuItems = React.useMemo<Array<AccountMenuItem>>(() => {
    const result: Array<AccountMenuItem> = accountMenu
      ? accountMenu.children[0].children.map((child) => ({
          cssClass: child.cssClass ?? '',
          //icon: child.iconName && isIconName(child.iconName) ? child.iconName : 'plus',
          icon: child.iconName ? child.iconName : 'plus',
          label: child.label ?? '',
          link: child.link ?? '',
          scrollTop: false,
          tags: child.tags,
        }))
      : [];

    result.push({
      icon: appConfig.icons?.logout,
      label: t('account.logout'),
      onClick: () => {
        logout();
      },
      scrollTop: false,
    });
    return result;
  }, [accountMenu, logout]);

  return (
    <>
      {accountMenu && session?.customer?.name ? (
        <PageWithSubpageContext
          pageSubtitle={t('account.welcomeMessage', { name: session.customer.name })}
          pageTitle={accountMenu.label}
        >
          <PageWithSubpageHeader />

          {renderUI({
            bs5: (
              <div className="container-xxl">
                <div className="row">
                  <div className="d-none d-lg-block col-lg-3">
                    <AccountMenu items={menuItems} />
                  </div>
                  <div className="col-lg-9">
                    {/* <Outlet /> */}
                    <Routes>
                      {page?.children?.map((child) => (
                        <Route
                          element={
                            session.customer && canUserAccessContent(session.customer, ['']) ? (
                              child.code === 'SITE_CCN_COUPONS' ? (
                                <AccountCouponsSubpage />
                              ) : child.code === 'SITE_CCN_CREDIT_CARDS' ? (
                                <AccountCreditCardsSubpage />
                              ) : child.code === 'SITE_CCN_HELP' ? (
                                <AccountHelpSubpage />
                              ) : child.code === 'SITE_CCN_INFO' ? (
                                <AccountInfoSubpage />
                              ) : child.code === 'SITE_CCN_ORDERS' ? (
                                <AccountOrdersSubpage enableReceiverColumn={enableReceiverColumn} />
                              ) : child.code === 'SITE_CCN_QUICK_REORDER' ? (
                                <AccountQuickReorderSubpage />
                              ) : child.code === 'SITE_CCN_PRIVACY' ? (
                                <AccountPrivacySubpage />
                              ) : child.code === 'SITE_CCN_WISHLIST' ? (
                                <AccountWishlistSubpage />
                              ) : child.code === 'SITE_CCN_TAX_BENEFITS' ? (
                                <AccountTaxBenefitsSubpage />
                              ) : child.code === 'SITE_CCN_LOYALTY' ? (
                                <AccountLoyaltySubpage />
                              ) : child.code === 'SITE_CCN_REFERRAL' ? (
                                <AccountReferralSubpage />
                              ) : customMappings ? (
                                Object.keys(customMappings).includes(child.code) ? (
                                  customMappings[child.code]
                                ) : (
                                  <></>
                                )
                              ) : (
                                <></>
                              )
                            ) : (
                              <p>{t('account.accessDenied')}</p>
                            )
                          }
                          key={child.code}
                          path={child.path}
                        />
                      ))}
                    </Routes>
                  </div>
                </div>
              </div>
            ),
          })}
        </PageWithSubpageContext>
      ) : (
        <div>
          {renderUI({
            bs5: (
              <div className="container-xxl">
                <div className="row justify-content-center">
                  <div className="col-md-8 col-lg-6 col-xl-4">
                    <div className="text-center mt-3 mb-4">
                      <h3 className=" fw-bold ">{t('account.login')}</h3>
                      <p>{t('account.insertCredentials')}</p>
                    </div>
                    <LoginForm />
                  </div>
                </div>
              </div>
            ),
          })}
        </div>
      )}
    </>
  );
};

interface AccountMenuProps {
  items: Array<AccountMenuItem>;
}

interface AccountMenuItem {
  cssClass?: string;
  icon?: string;
  label: string;
  link?: string;
  onClick?(): void;
  scrollTop?: boolean;
  tags?: Array<string>;
}

const AccountMenu = ({ items }: AccountMenuProps) => {
  const { session } = useAppContext();

  return renderUI({
    bs5: (
      <div className="menu-account">
        <div className="list-group">
          {items.map(({ cssClass, icon, label, link, onClick, scrollTop, tags }) => {
            const canUserAccess = session?.customer && canUserAccessContent(session.customer, tags);
            return (
              <>
                {canUserAccess && (
                  <>
                    {link ? (
                      <NavLink
                        className={classnames(
                          cssClass,
                          'text-reset text-decoration-none border-0 small list-group-item list-group-item-action hstack gap-5 py-3 px-3 px-xl-5 mb-1',
                        )}
                        key={label}
                        scrollTop={scrollTop}
                        silent
                        to={link}
                      >
                        {icon && <Icon name={icon} size={25} />}
                        {label}
                      </NavLink>
                    ) : (
                      <Button
                        className={classnames(
                          cssClass,
                          'text-reset text-decoration-none border-0 small list-group-item list-group-item-action hstack gap-5 py-3 px-3 px-xl-5 mb-1',
                        )}
                        iconStart={icon ? { name: icon, size: 25 } : undefined}
                        justifyContent="start"
                        key={label}
                        label={label}
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick={() => {
                          onClick && onClick();
                        }}
                      />
                    )}
                  </>
                )}
              </>
            );
          })}
        </div>
      </div>
    ),
  });
};
